import { Upload, CheckCircle2, XCircle, RotateCcw, ListChecks, SlidersHorizontal, Activity, LogOut } from "lucide-react";

import { formatDate } from "@/utils/dateUtils";
import { cn } from "@/lib/utils";

/**
 * One compliance event, as the dashboard's Live Activity Feed and the Audit Log
 * page both render it.
 *
 * Extracted from `dashboard/index.jsx` when the Audit Log page arrived. The two
 * surfaces show the same rows — the feed a recent digest, the page the full
 * filterable trail — so a second copy of this markup would be two renderings of
 * one event, drifting apart on the first change to either.
 *
 * Keys of ACTIVITY_META are the server's `category`, not its `action`: the
 * service groups actions into presentation categories precisely so the client
 * never has to know the action vocabulary, and a newly-added action lands in a
 * category that already renders.
 */

// Icon + tint per category. `settings` is its own entry rather than falling
// into `other`: a settings change moves the approval bar for every candidate in
// the tenant, so it should not share a tint with genuinely unrecognised events.
//
// Tones, not raw hex: `tone-*` is the app's theme-aware accent palette
// (`tailwind.config.js`, `themes/styles/tones.js`), so these tints hold their
// contrast across the light/dark toggle instead of painting the same value in
// both themes. Written as full class names, not `bg-tone-${tone}-soft`
// template strings — Tailwind's scanner generates only the utility classes it
// finds spelled out literally in source.
export const ACTIVITY_META = {
  upload: { icon: Upload, iconBg: "bg-tone-blue-soft", iconText: "text-tone-blue" },
  verified: { icon: CheckCircle2, iconBg: "bg-tone-emerald-soft", iconText: "text-tone-emerald" },
  rejected: { icon: XCircle, iconBg: "bg-tone-rose-soft", iconText: "text-tone-rose" },
  request: { icon: RotateCcw, iconBg: "bg-tone-amber-soft", iconText: "text-tone-amber" },
  // Release ends a run without judging anyone: slate, so it reads as neither
  // the amber of a pending request nor the red of a rejection. LogOut matches
  // the action's icon on the candidate detail page and in the audit timeline.
  released: { icon: LogOut, color: "#64748b" },
  checklist: { icon: ListChecks, iconBg: "bg-tone-purple-soft", iconText: "text-tone-purple" },
  settings: { icon: SlidersHorizontal, iconBg: "bg-tone-violet-soft", iconText: "text-tone-violet" },
  other: { icon: Activity, iconBg: "bg-tone-slate-soft", iconText: "text-tone-slate" },
};

// The actor chip is the point of an audit row: a candidate acting through their
// own portal link carries very different weight from a staff member, and the
// two must be visibly distinct when someone reviews a decision.
//
// Only the three non-staff kinds are special-cased. Staff roles are
// tenant-authored rows ("Admin", "Recruiter", …), so the server sends the real
// role name and it renders verbatim — never collapsed into a generic label,
// which would tell the reader nothing about who acted.
const ROLE_CHIP = {
  candidate: { label: "Candidate", className: "bg-tone-blue-soft text-tone-blue border-tone-blue/25" },
  superadmin: { label: "Platform admin", className: "bg-tone-purple-soft text-tone-purple border-tone-purple/25" },
  system: { label: "System", className: "bg-background-tertiary text-text-secondary border-border-primary" },
};

export const roleChip = (role) =>
  ROLE_CHIP[role] ?? {
    label: (role ?? "").replace(/_/g, " ") || "Staff",
    className: "bg-background-form/60 text-text-primary/70 border-border-primary/60",
  };

/**
 * @param {object}  props
 * @param {object}  props.item        One row from the feed or the audit log.
 * @param {boolean} [props.showActor] Render the actor's role chip. The feed
 *   shows the actor's name inline and stays compact; the audit log is evidence
 *   and names the role explicitly.
 * @param {string}  [props.className]
 */
const ActivityRow = ({ item, showActor = false, className }) => {
  const meta = ACTIVITY_META[item?.category] ?? ACTIVITY_META.other;
  const Icon = meta.icon;
  // Both shapes are served: the feed sends `actorName` + `at`, the audit log
  // sends a full `actor` object + `timestamp`. Reading both here is what lets
  // one component serve both without either endpoint changing shape.
  const actorName = item?.actor?.name ?? item?.actorName;
  const actorRole = item?.actor?.role;
  const at = item?.timestamp ?? item?.at;
  const chip = showActor && actorRole ? roleChip(actorRole) : null;

  return (
    <div
      className={cn(
        "flex items-start gap-3 py-2.5 px-1 border-b border-border-primary/60 last:border-b-0 hover:bg-background-form/20 rounded-lg transition-colors",
        className,
      )}
    >
      <div className={cn("w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5", meta.iconBg)}>
        <Icon size={13} className={meta.iconText} />
      </div>

      <div className="min-w-0 flex-1">
        <p className="text-xs text-text-primary leading-snug truncate">{item?.title}</p>

        <div className="flex items-center gap-1.5 flex-wrap">
          <p className="text-[10px] text-text-secondary truncate">
            {item?.candidateName ? item.candidateName : "—"}
            {actorName ? ` · by ${actorName}` : ""}
          </p>
          {chip && (
            <span
              className={cn(
                // No `capitalize`: role names are tenant-authored and render
                // exactly as the tenant wrote them.
                "px-1.5 py-0.5 rounded-md border text-[9.5px] font-semibold",
                chip.className,
              )}
            >
              {chip.label}
            </span>
          )}
        </div>

        {/* The "why": a rejection reason or re-request instruction. Only the
            audit log carries it — the feed's rows have no note field. */}
        {item?.note && (
          <p className="mt-1 rounded-lg border border-border-primary bg-background-form/30 px-2.5 py-1.5 text-[11px] text-text-primary/80 whitespace-pre-wrap break-words">
            {item.note}
          </p>
        )}
      </div>

      {at && <span className="text-[10px] text-text-secondary shrink-0 whitespace-nowrap">{formatDate(at)}</span>}
    </div>
  );
};

export default ActivityRow;
